<div class="ticket-details-container" [class.comments-open]="showComments">
  <div class="dialog-header">
    <h2>Ticket Details</h2>
    <div class="header-actions">
      <button mat-icon-button (click)="closeDialog()" class="close-button">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <div class="dialog-content">
    <div class="main-content">
      <div *ngIf="ticketDetails$ | async as ticket" class="ticket-content">
      <div class="ticket-header">
        <div class="ticket-id-status">
          <h3>Ticket #{{ ticket.id }}</h3>
          <span class="status-badge" [class]="getStatusClass(ticket.ticketStatusName)">
            {{ ticket.ticketStatusName }}
          </span>
        </div>
        <div class="ticket-meta-readonly">
          <div class="meta-row">
            <span class="label">Category:</span>
            <span class="value">{{ ticket.ticketCategoryName }}</span>
          </div>
          <div class="meta-row" *ngIf="ticket.organizationId">
            <span class="label">Organization:</span>
            <span class="value">{{ getOrganizationDisplayValue(ticket.organizationId) }}</span>
          </div>
          <div class="meta-row">
            <span class="label">Date Created:</span>
            <span class="value">{{ formatDate(ticket.createDate) }}</span>
          </div>
          <div class="meta-row" *ngIf="ticket.modifyDate">
            <span class="label">Last Modified:</span>
            <span class="value">{{ formatDate(ticket.modifyDate) }}</span>
          </div>
          <div class="meta-row" *ngIf="isTicketClosed(ticket.ticketStatusName) && ticket.closedDate">
            <span class="label">Closed Date:</span>
            <span class="value">{{ formatDate(ticket.closedDate) }}</span>
          </div>
          <div class="meta-row" *ngIf="ticket.submittedByName">
            <span class="label">Submitted By:</span>
            <span class="value">{{ ticket.submittedByName }} ({{ ticket.submittedByEmail }})</span>
          </div>
          <div class="meta-row" *ngIf="ticket.patientLastName">
            <span class="label">Patient Name:</span>
            <span class="value">{{ ticket.patientFirstName }} {{ ticket.patientLastName }}</span>
          </div>
          <div class="meta-row" *ngIf="ticket.measureTitle">
            <span class="label">Measure Name:</span>
            <span class="value">{{ ticket.measureCode }} - {{ ticket.measureTitle }}</span>
          </div>
          <div class="meta-row" *ngIf="ticket.missingMeasureCodes?.length">
            <span class="label">Measure(s):</span>
            <span class="value">{{ getMissingMeasureTitles(ticket.missingMeasureCodes) }}</span>
          </div>
          <div class="meta-row" *ngIf="ticket.patientMRN">
            <span class="label">Patient MRN:</span>
            <span class="value">{{ ticket.patientMRN }}</span>
          </div>
        </div>
      </div>

      <mat-divider></mat-divider>

      <div class="edit-form">
        <form [formGroup]="editForm" (ngSubmit)="updateTicket()">
          <h4>Edit Ticket</h4>
          
          <div *ngIf="(categoriesLoading$ | async) || (statusesLoading$ | async)" class="loading-forms">
            <mat-spinner diameter="30"></mat-spinner>
            <span>Loading form data...</span>
          </div>

          <div *ngIf="!((categoriesLoading$ | async) || (statusesLoading$ | async))">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Summary</mat-label>
              <input matInput formControlName="subject" placeholder="Enter summary">
              <mat-hint align="end">{{ getCharacterCount('subject') }}/{{ VALIDATION_LIMITS.SUBJECT_MAX }}</mat-hint>
              <mat-error *ngIf="editForm.get('subject')?.hasError('required')">
                Summary is required
              </mat-error>
              <mat-error *ngIf="editForm.get('subject')?.hasError('maxlength')">
                Summary cannot exceed {{ VALIDATION_LIMITS.SUBJECT_MAX }} characters
              </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Description</mat-label>
              <textarea matInput formControlName="description" 
                       placeholder="Enter description" 
                       rows="4"></textarea>
              <mat-hint align="end">{{ getCharacterCount('description') }}/{{ VALIDATION_LIMITS.DESCRIPTION_MAX }}</mat-hint>
              <mat-error *ngIf="editForm.get('description')?.hasError('maxlength')">
                Description cannot exceed {{ VALIDATION_LIMITS.DESCRIPTION_MAX }} characters
              </mat-error>
            </mat-form-field>

            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>Category</mat-label>
                <input matInput 
                       [value]="getSelectedCategoryName()" 
                       readonly>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Status</mat-label>

                <mat-select *ngIf="isSuperUser()" formControlName="ticketStatusId">
                  <mat-option *ngFor="let status of statuses" [value]="status.id">
                    {{ status.name }}
                  </mat-option>
                </mat-select>

                <input *ngIf="!isSuperUser()" 
                       matInput 
                       [value]="getSelectedStatusName()" 
                       readonly>
                
                <mat-error *ngIf="isSuperUser() && editForm.get('ticketStatusId')?.hasError('required')">
                  Status is required
                </mat-error>
              </mat-form-field>
            </div>

            <div class="form-actions">
              <button mat-raised-button 
                      color="primary" 
                      type="submit" 
                      [disabled]="editForm.invalid || isUpdating">
                <mat-spinner diameter="20" *ngIf="isUpdating"></mat-spinner>
                <span *ngIf="!isUpdating">Save Ticket</span>
                <span *ngIf="isUpdating">Updating...</span>
              </button>
              <button mat-button type="button" (click)="closeDialog()" [disabled]="isUpdating">
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>

      <mat-divider></mat-divider>

      <div class="attachments-section">
        <h4>Attachments</h4>
      
        <div *ngIf="ticket.ticketAttachments && ticket.ticketAttachments.length > 0" class="attachments-list">
          <div *ngFor="let attachment of ticket.ticketAttachments"
              class="attachment-item"
              [class.downloading]="downloadingAttachment$ | async"
              [class.previewing]="previewingAttachment$ | async"
              tabindex="0"
              [attr.aria-label]="'Download or preview ' + attachment.originalFileName">
            <mat-icon class="attachment-icon">attach_file</mat-icon>
            <div class="attachment-info">
              <span class="attachment-name">{{ attachment.originalFileName }}</span>
              <div class="attachment-meta">
                <span>{{ attachment.createdByName || 'Unknown' }} • {{ formatDate(attachment.createDate) }}</span>
                <mat-chip *ngIf="attachment.isPhi" class="phi-chip" color="warn">PHI</mat-chip>
              </div>

              <!-- Display mode -->
              <div class="attachment-doc-meta" *ngIf="editingAttachmentId !== attachment.id">
                <span *ngIf="attachment.documentType" class="doc-type-label">{{ attachment.documentType }}</span>
                <span *ngIf="attachment.documentDate" class="doc-date-label">{{ attachment.documentDate | date:'mediumDate' }}</span>
                <button mat-icon-button
                        title="Edit document type / date"
                        (click)="startEditAttachment(attachment)">
                  <mat-icon>edit</mat-icon>
                </button>
              </div>

              <!-- Edit mode -->
              <div class="attachment-doc-edit" *ngIf="editingAttachmentId === attachment.id">
                <div class="doc-edit-fields">
                  <mat-form-field appearance="outline" class="doc-type-field">
                    <mat-label>Document Type</mat-label>
                    <mat-select [(ngModel)]="attachmentEditValues[attachment.id].dataEnrichmentDocumentType" [ngModelOptions]="{standalone: true}">
                      <mat-option *ngFor="let type of documentTypes" [value]="type">{{ type }}</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="doc-date-field">
                    <mat-label>Document Date</mat-label>
                    <input matInput type="date" [max]="todayStr" [(ngModel)]="attachmentEditValues[attachment.id].dataEnrichmentDocumentDate" [ngModelOptions]="{standalone: true}">
                    <mat-hint>The Document Date reflects when the clinical activity occurred or documentation was produced.</mat-hint>
                  </mat-form-field>
                </div>
                <div class="doc-edit-actions">
                  <button mat-icon-button
                          color="primary"
                          title="Save"
                          [disabled]="!attachmentEditValues[attachment.id].dataEnrichmentDocumentType || !attachmentEditValues[attachment.id].dataEnrichmentDocumentDate || !hasAttachmentChanged(attachment.id)"
                          (click)="saveAttachmentMetadata(attachment.id)">
                    <mat-icon>check</mat-icon>
                  </button>
                  <button mat-icon-button title="Cancel" (click)="cancelEditAttachment()">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </div>
            <div class="download-action">
              <button mat-icon-button
                      *ngIf="isPreviewable(attachment.mediaType)"
                      (click)="previewAttachment(attachment.id, attachment.originalFileName)"
                      [disabled]="(previewingAttachment$ | async)">
                <mat-spinner diameter="20" *ngIf="previewingAttachment$ | async"></mat-spinner>
                <mat-icon *ngIf="!(previewingAttachment$ | async)">visibility</mat-icon>
              </button>

              <button mat-icon-button
                      (click)="downloadAttachment(attachment.id, attachment.originalFileName)"
                      [disabled]="(downloadingAttachment$ | async)">
                <mat-spinner diameter="20" *ngIf="downloadingAttachment$ | async"></mat-spinner>
                <mat-icon *ngIf="!(downloadingAttachment$ | async)">download</mat-icon>
              </button>
              <button mat-icon-button
                      (click)="deleteAttachment(attachment.id, attachment.originalFileName)"
                      color="warn"
                      title="Delete attachment">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </div>
        </div>
        <div *ngIf="!ticket.ticketAttachments || ticket.ticketAttachments.length === 0">
          <p>No attachments yet.</p>
        </div>
        <div class="add-attachment-button" style="margin-top: 16px;">
          <button mat-raised-button color="primary" (click)="openAddAttachmentModal()">
            <mat-icon>attach_file</mat-icon>
            Add Attachment
          </button>
        </div>

        <mat-divider></mat-divider>
      </div>

      <div class="comments-section">
        <h4>Comments ({{ (ticketComments$ | async)?.length || 0 }})</h4>
        
        <div class="add-comment-section">
          <form [formGroup]="commentForm" (ngSubmit)="addComment()">
            <mat-form-field class="full-width">
              <mat-label>Add a comment</mat-label>
              <textarea 
                matInput 
                formControlName="comment"
                rows="3"
                placeholder="Enter your comment here...">
              </textarea>
              <mat-hint align="end">{{ getCommentCharacterCount() }}/{{ VALIDATION_LIMITS.COMMENT_MAX }}</mat-hint>
              <mat-error *ngIf="commentForm.get('comment')?.hasError('maxlength')">
                Comment cannot exceed {{ VALIDATION_LIMITS.COMMENT_MAX }} characters
              </mat-error>
            </mat-form-field>
            
            <div class="comment-actions">
              <button 
                mat-raised-button 
                color="primary" 
                type="submit"
                [disabled]="isCommentFormInvalid"
                class="add-comment-button">
                <mat-icon *ngIf="isAddingComment">hourglass_empty</mat-icon>
                <mat-icon *ngIf="!isAddingComment">send</mat-icon>
                {{ isAddingComment ? 'Adding...' : 'Add Comment' }}
              </button>
            </div>
          </form>
        </div>

        <div class="comments-chat" #commentsContainer>
          <div *ngFor="let comment of (ticketComments$ | async); trackBy: trackByCommentId" 
               class="chat-message">
            <div class="message-avatar">
              <mat-icon>account_circle</mat-icon>
            </div>
            <div class="message-content">
              <div class="message-header">
                <span class="author-name">{{ comment.createdByName || 'Anonymous' }}</span>
                <span class="message-time">{{ formatDate(comment.createDate) }}</span>
                <button mat-icon-button 
                        class="delete-comment-btn"
                        (click)="deleteComment(comment.id)"
                        title="Delete comment">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
              <div class="message-text">
                {{ comment.comment }}
              </div>
            </div>
          </div>

          <div *ngIf="(ticketComments$ | async)?.length === 0" class="no-comments">
            <mat-icon>chat_bubble_outline</mat-icon>
            <p>No comments yet. Start the conversation!</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>